import { makeAutoObservable, reaction, runInAction } from "mobx";
import sleep from "sleep-promise";
import { createLogger } from "@/utils/log";
import { consumeWS, WsHandle } from "@/utils/api";
import { Router } from "@/api/router";
import { SessionStore, ProjectStore } from "@/app_store/agent";
import { RpcError } from "@/app_store/rpc_error";
import "@/app_store/errors"; // side-effect: registers generated typed RPC errors

/** Recursively convert a MobX store tree to a plain JSON-serializable object.
 *
 * JSON.stringify on MobX 6 class instances is unreliable: observable.deep may
 * wrap plain arrays/objects in ObservableArray/ObservableObject proxies whose
 * enumeration and toJSON behaviour differs across MobX versions and strict-mode
 * settings.  This function enumerates OWN string-keyed properties explicitly,
 * strips the store-internal fields (_transport, _chainPath), and recurses into
 * arrays (Array.isArray covers ObservableArray in MobX 6) and plain objects
 * with a visited-set to catch any unexpected circular references.
 */
function serializeStore(value: any, seen = new Set<any>()): any {
    if (value === null || value === undefined) return value;
    if (typeof value !== "object") return value;
    if (seen.has(value)) return null;
    seen.add(value);
    try {
        if (Array.isArray(value)) {
            const arr = Array.from(value).map((item: any) => serializeStore(item, seen));
            seen.delete(value);
            return arr;
        }
        const out: Record<string, any> = {};
        for (const key of Object.keys(value)) {
            if (key === "_transport" || key === "_chainPath") continue;
            try { out[key] = serializeStore(value[key], seen); } catch { /* skip */ }
        }
        seen.delete(value);
        return out;
    } catch {
        seen.delete(value);
        return null;
    }
}

export interface ListDirEntry {
    path: string;
    is_dir: boolean;
    is_empty: boolean;
    modified_at: number;
}

export interface GitWatchPath {
    repo_path: string;
    workspace: string;
    worktree?: string | null;
}

const LOG = createLogger("agent");

const CHUNK_SIZE = 512 * 1024;

interface PendingRequest {
    mode: "request" | "stream_request";
    resolve: (value: any) => void;
    reject: (err: Error) => void;
    onItem?: (item: any) => void;
    onDone?: () => void;
    itemBuffer: any[];
}

export class AgentStore {
    isConnected: boolean = false;
    isOperationPending: boolean = false;
    // The canonical Session (see @/app_store/agent/Session.generated) -- kept live by
    // applying every `session_change` push (@/api/handlers/session.ts) streamed from
    // agent.app.session.Session.stream_changes() via agent/agent/api/producers/session.py.
    // Rebuilt fresh on each (re)connect since a new connection means a new server-side
    // Session, whose mutations only start streaming from that point on.
    session: SessionStore;

    private _pendingRpcCount: number = 0;
    private _handlersRouter: Router<AgentStore>;
    private _producersRouter: Router<any>;
    private _root: any;
    private _wsConn: WsHandle | null = null;
    private _connectGen: any = null;
    private _producerGens: any[] = [];
    private _token: string | null = null;
    private _apiUrl: string = "";
    private _visibilityListenerAttached: boolean = false;
    private _lastSessionSnapshot: object | null = null;
    private _watchPayload: object | null = null;
    private _gitWatchPayload: object | null = null;
    // Keyed by request_id. Both request() and streamRequest() send msg_type
    // "request" on the wire -- mode only affects how a reply is consumed here.
    private _pendingRequests: Map<string, PendingRequest> = new Map();
    // Chunk reassembly buffer for incoming frames, keyed by chunk_id.
    private _chunkBuffer: Map<string, string> = new Map();
    // RPC calls that have not yet been acknowledged by the server. Each entry
    // survives disconnects and is re-sent on the next successful reconnect so
    // callers never need to handle transport failures themselves.
    private _rpcQueue: Array<{
        request_id: string;
        data: object;
        resolve: (value: any) => void;
        reject: (err: Error) => void;
    }> = [];

    constructor(
        handlers_router: Router<AgentStore>,
        producers_router: Router<any>,
        root: any,
    ) {
        this._handlersRouter = handlers_router;
        this._producersRouter = producers_router;
        this._root = root;
        this.session = this._freshSession();
        makeAutoObservable(this, {
            _pendingRpcCount: false,
            _handlersRouter: false,
            _producersRouter: false,
            _root: false,
            _wsConn: false,
            _connectGen: false,
            _producerGens: false,
            _token: false,
            _apiUrl: false,
            _watchPayload: false,
            _gitWatchPayload: false,
            _lastSessionSnapshot: false,
            _pendingRequests: false,
            _chunkBuffer: false,
            _rpcQueue: false,
            _visibilityListenerAttached: false,
        } as any);
    }

    connect(token: string, apiUrl: string) {
        LOG("connect", apiUrl);
        this._connectGen?.cancel();
        this._token = token;
        this._apiUrl = apiUrl;
        this._connectGen = this._connectLoop();
        if (!this._visibilityListenerAttached) {
            this._visibilityListenerAttached = true;
            const abort = () => { if (this._token) this._wsConn?.abort(); };
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") abort();
            });
            window.addEventListener("online", abort);
        }
    }

    disconnect() {
        LOG("disconnect");
        this._token = null;
        this._connectGen?.cancel();
        this._connectGen = null;
    }

    async request<T = any>(data: object): Promise<T> {
        LOG("->", (data as any).type ?? data);
        const isRpc = (data as any).type === "rpc_call";
        if (isRpc) {
            runInAction(() => {
                this._pendingRpcCount++;
                this.isOperationPending = true;
            });
            try {
                return await new Promise<T>((resolve, reject) => {
                    const request_id = crypto.randomUUID();
                    this._rpcQueue.push({ request_id, data, resolve, reject });
                    this._flushRpcQueue();
                });
            } finally {
                runInAction(() => {
                    this._pendingRpcCount--;
                    this.isOperationPending = this._pendingRpcCount > 0;
                });
            }
        }
        return await new Promise<T>((resolve, reject) => {
            const request_id = crypto.randomUUID();
            this._pendingRequests.set(request_id, { mode: "request", resolve, reject, itemBuffer: [] });
            this._sendRequest(request_id, data);
        });
    }

    async *streamRequest<T = any>(data: object): AsyncGenerator<T, void, unknown> {
        LOG("->stream", (data as any).type ?? data);
        const request_id = crypto.randomUUID();
        const queue: Array<T | null> = [];
        let error: Error | null = null;
        let waiter: (() => void) | null = null;

        const push = (item: T | null) => {
            queue.push(item);
            waiter?.();
            waiter = null;
        };

        this._pendingRequests.set(request_id, {
            mode: "stream_request",
            resolve: () => {},
            reject: (err: Error) => { error = err; push(null); },
            onItem: (item: any) => push(item),
            onDone: () => push(null),
            itemBuffer: [],
        });

        this._sendRequest(request_id, data);

        try {
            while (true) {
                while (queue.length > 0) {
                    const item = queue.shift()!;
                    if (item === null) {
                        if (error) throw error;
                        return;
                    }
                    yield item;
                }
                await new Promise<void>(r => { waiter = r; });
            }
        } finally {
            this._pendingRequests.delete(request_id);
        }
    }

    closeSocket() {
        this._wsConn?.close();
    }

    getProjectRoot(): string | undefined {
        return this._root.project.selectedProject?.relative_path;
    }

    watch(paths: string[]) {
        // TODO(app-model): "update_watch" no longer has a backend handler (old
        // agent.api.handlers.watch was removed); no equivalent exists yet in the app model.
    }

    gitWatch(gitPaths: GitWatchPath[]) {
        // TODO(app-model): "git_update_watch" no longer has a backend handler (old
        // agent.api.handlers.watch was removed); no equivalent exists yet in the app model.
    }

    getProject(): ProjectStore | null {
        return this.session.project;
    }

    private _flushRpcQueue() {
        if (!this._wsConn) return;
        for (const entry of this._rpcQueue) {
            if (!this._pendingRequests.has(entry.request_id)) {
                this._pendingRequests.set(entry.request_id, {
                    mode: "request", resolve: entry.resolve, reject: entry.reject, itemBuffer: [],
                });
                this._sendRequest(entry.request_id, entry.data);
            }
        }
    }

    /** Serialize `payload` and emit it as one or more chunked envelope frames
     * (512 KB slices sharing one chunk_id), with msg_type "request" and
     * req_type (mirroring payload.type, if present) stamped on every frame. */
    private _sendRequest(request_id: string, payload: object) {
        if (!this._wsConn) return;
        const msg_type = "request";
        const req_type = (payload as any)?.type;
        const dataStr = JSON.stringify(payload);
        const chunk_id = crypto.randomUUID();
        if (dataStr.length <= CHUNK_SIZE) {
            this._wsConn.send(JSON.stringify({ request_id, msg_type, req_type, chunk_id, chunk_end: true, data: dataStr }));
            return;
        }
        for (let i = 0; i < dataStr.length; i += CHUNK_SIZE) {
            const chunk_end = i + CHUNK_SIZE >= dataStr.length;
            this._wsConn.send(JSON.stringify({ request_id, msg_type, req_type, chunk_id, chunk_end, data: dataStr.slice(i, i + CHUNK_SIZE) }));
        }
    }

    private _freshSession(): SessionStore {
        return new SessionStore(
            { _entity_class_name: "Session", project: null, container: null, vault: null },
            this,
        );
    }

    private _serializeSessionSnapshot(): object | null {
        if (!this.session.project) return null;
        try {
            return serializeStore(this.session);
        } catch (err) {
            LOG("_serializeSessionSnapshot failed:", err);
            return null;
        }
    }

    private _launchProducers() {
        this._producerGens = this._producersRouter.producers.map((fn: any) => fn(this));
    }

    private _cancelProducers() {
        for (const gen of this._producerGens) gen?.cancel();
        this._producerGens = [];
    }

    private async _connectLoop() {
        while (true) {
            // Snapshot current session state before this attempt so the agent can
            // reconcile on reconnect (the snapshot from the previous connection).
            this._lastSessionSnapshot = this._serializeSessionSnapshot();

            const wsBase = this._apiUrl
                .replace(/^https:/, "wss:")
                .replace(/^http:/, "ws:");
            const url = `${wsBase}/ws/agent/client/?token=${encodeURIComponent(this._token!)}`;
            LOG("connecting", wsBase);

            this._wsConn = consumeWS(url, this._handleMessage.bind(this), {
                onOpen: () => {
                    LOG("connected");
                    // Send snapshot BEFORE setting isConnected=true. Setting isConnected=true
                    // fires MobX reactions synchronously (e.g. ProjectStore's isConnected
                    // reaction calls selectProject → request() → _flushRpcQueue()), which
                    // would send RPC messages before the snapshot and corrupt message ordering
                    // — the backend reads the first message as the snapshot, so an RPC
                    // arriving first causes it to start fresh (state=None) on every reconnect.
                    LOG("sending snapshot", this._lastSessionSnapshot);
                    const _snapshotStr = JSON.stringify(this._lastSessionSnapshot);
                    const _snapshotChunkId = crypto.randomUUID();
                    let _i = 0;
                    while (_i < _snapshotStr.length || _i === 0) {
                        let _end = Math.min(_i + CHUNK_SIZE, _snapshotStr.length);
                        let _frame: string;
                        // Shrink slice until the serialized envelope fits within CHUNK_SIZE.
                        // JSON.stringify escapes embedded quotes/backslashes so the wire size
                        // can exceed the raw slice length — shrink proportionally until it fits.
                        while (true) {
                            _frame = JSON.stringify({
                                msg_type: "request",
                                req_type: "session_snapshot",
                                chunk_id: _snapshotChunkId,
                                chunk_end: _end >= _snapshotStr.length,
                                data: _snapshotStr.slice(_i, _end),
                            });
                            if (_frame.length <= CHUNK_SIZE || _end <= _i + 1) break;
                            _end = _i + Math.max(1, Math.floor((_end - _i) * (CHUNK_SIZE / _frame.length) * 0.9));
                        }
                        this._wsConn!.send(_frame);
                        if (_end >= _snapshotStr.length) break;
                        _i = _end;
                    }
                    runInAction(() => {
                        this.isConnected = true;
                        // Do NOT reset this.session here: keep existing observable state so
                        // components see no empty-state flash. The agent will reconcile to
                        // match our snapshot and then stream the initial state via
                        // session_producer, whose `add` ops will overwrite each field in place.
                    });
                    if (this._watchPayload) this.request(this._watchPayload);
                    if (this._gitWatchPayload) this.request(this._gitWatchPayload);
                    this._launchProducers();
                    this._flushRpcQueue();
                },
            });
            try {
                await this._wsConn.consume();
            } finally {
                this._cancelProducers();
                this._wsConn?.close();
                this._wsConn = null;
                this._chunkBuffer.clear();
                // Resolve only non-rpc pending request()s with null (existing behaviour);
                // reject streamRequest()s. RPC entries stay in _rpcQueue and will be
                // re-sent on reconnect so their callers transparently wait without ever
                // seeing a transport error.
                const rpcIds = new Set(this._rpcQueue.map(e => e.request_id));
                this._pendingRequests.forEach((entry, id) => {
                    if (rpcIds.has(id)) return;
                    if (entry.mode === "request") {
                        entry.resolve(null);
                    } else {
                        entry.reject(new RpcError("Disconnected", "disconnected"));
                    }
                });
                // Clear ALL entries from _pendingRequests — RPC entries are re-added and
                // re-sent by _flushRpcQueue() on reconnect. Leaving them here causes
                // _flushRpcQueue to skip them (the has() check), so they're never re-sent
                // and _pendingRpcCount stays permanently elevated, blocking _initSession.
                this._pendingRequests.clear();
                runInAction(() => {
                    this.isConnected = false;
                });
            }
            await sleep(1_000);
        }
    }

    /** Reassemble chunked envelope frames and dispatch the completed payload. */
    private _handleMessage(envelope: any) {
        const chunkId: string = envelope.chunk_id;
        const buffered = (this._chunkBuffer.get(chunkId) ?? "") + (envelope.data ?? "");
        if (!envelope.chunk_end) {
            this._chunkBuffer.set(chunkId, buffered);
            return;
        }
        this._chunkBuffer.delete(chunkId);
        let payload: any;
        try {
            payload = JSON.parse(buffered);
        } catch {
            return;
        }
        this._dispatchBody(envelope.request_id, envelope.msg_type, payload);
    }

    private _dispatchBody(requestId: string | undefined, msgType: string, payload: any) {
        const entry = requestId ? this._pendingRequests.get(requestId) : undefined;
        if (!entry) {
            // Server-initiated push (change_event) -- no request_id we're tracking.
            LOG("<-", payload?.type ?? payload);
            runInAction(() => this._handlersRouter.dispatch(payload, this._root));
            return;
        }
        const forget = () => {
            this._pendingRequests.delete(requestId!);
            this._rpcQueue = this._rpcQueue.filter(e => e.request_id !== requestId);
        };
        switch (msgType) {
            case "error":
                forget();
                entry.reject(RpcError.fromPayload(payload));
                return;
            case "response":
                forget();
                if (entry.mode === "request") {
                    entry.resolve(payload);
                } else {
                    entry.onItem?.(payload);
                    entry.onDone?.();
                }
                return;
            case "stream_response":
                if (entry.mode === "request") {
                    entry.itemBuffer.push(payload);
                } else {
                    entry.onItem?.(payload);
                }
                return;
            case "stream_response_done":
                forget();
                if (entry.mode === "request") {
                    entry.resolve(entry.itemBuffer);
                } else {
                    entry.onDone?.();
                }
                return;
        }
    }
}
